"use client";
import React, { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { ClipboardCheck, AlertCircle, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardFilters from "../../Component/dashboard-filters";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "../../Component/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../Component/ui/chart";
import API_URL from "../../../src/Config";
import "./allSection.css";

export function AuditsSection() {
  const navigate = useNavigate();
  
  const [summary, setSummary] = useState({
    total_audits: { count: 0, label: "" },
    total_open_points: { count: 0, label: "" },
    total_closed_points: { count: 0, closure_rate: 0, label: "" },
  });

  const [auditPointsSummary, setAuditPointsSummary] = useState({
    total_points: 0,
    open_points: 0,
    closed_points: 0,
  });
  const [auditClosureTrend, setAuditClosureTrend] = useState([]);
  const [auditWisePointStatus, setAuditWisePointStatus] = useState([]);
  const [filters, setFilters] = useState({
    department_id: "",
    date_preset: "all",
    showCustomDate: false,
    start_date: "",
    end_date: "",
  });
  const [loading, setLoading] = useState(true);

  const fetchAuditKPIs = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const authKey = localStorage.getItem("authKey");
      const params = new URLSearchParams();

      if (
        currentFilters.date_preset &&
        currentFilters.date_preset !== "custom"
      ) {
        params.append("filter_option", currentFilters.date_preset);
      }
      if (currentFilters.department_id)
        params.append("department_id", currentFilters.department_id);
      if (currentFilters.start_date)
        params.append("start_date", currentFilters.start_date);
      if (currentFilters.end_date)
        params.append("end_date", currentFilters.end_date);

      const res = await fetch(
        `${API_URL}/api/kpi/audit-management?${params.toString()}`,
        {
          headers: { Authorization: authKey || "" },
        }
      );
      if (!res.ok) throw new Error("Failed to fetch audit KPIs");
      const data = await res.json();

      setSummary(data.summary);
      setAuditPointsSummary(data.charts.audit_points_summary);
      setAuditClosureTrend(data.charts.audit_closure_trend);
      setAuditWisePointStatus(data.charts.audit_wise_point_status);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchAuditKPIs(newFilters);
  };

  const handleResetFilters = () => {
    const reset = {
      department_id: "",
      date_preset: "All",
      showCustomDate: false,
      start_date: "",
      end_date: "",
    };
    setFilters(reset);
    fetchAuditKPIs(reset);
  };

  useEffect(() => {
    fetchAuditKPIs();
  }, []);

  // Card click handler
  const handleCardClick = (listType) => {
    const params = new URLSearchParams();
    params.append("list_type", listType);
    
    const filterOptionValue = filters.date_preset ? filters.date_preset : "All";
    params.append("filter_option", filterOptionValue);
    
    if (filters.department_id && filters.department_id.trim() !== "") {
      params.append("department_id", filters.department_id);
    }
    
    if (filters.start_date && filters.end_date && 
        filters.start_date.trim() !== "" && filters.end_date.trim() !== "") {
      params.append("start_date", filters.start_date);
      params.append("end_date", filters.end_date);
    }

    console.log("Navigating to Audit List with params:", params.toString());
    
    window.open(`/audit-list?${params.toString()}`, "_blank");
  };

  if (loading)
    return (
      <div className="loader-container">
        <div className="loader"></div>Loading...
      </div>
    );

  return (
    <div className="newdashboard-audit-section">
      {/* Header */}
      <div className="newdashboard-audit-header">
        <div>
          <h2 className="newdashboard-section-title">Audit Management</h2>
          <p>Track audit findings and closure status</p>
        </div>
        <DashboardFilters
          filters={filters}
          onFilterChange={setFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      </div>

      {/* KPI Cards - Now Clickable */}
      <div className="newdashboard-audit-kpis">
        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("audits")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-audit-card-header">
            <CardTitle className="newdashboard-audit-card-title">
              Total Audits
            </CardTitle>
            <ClipboardCheck className="newdashboard-audit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-audit-card-value newdashboard-audit-total">
              {summary.total_audits.count}
            </div>
            <p className="newdashboard-audit-note">
              {summary.total_audits.label}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("open_points")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-audit-card-header">
            <CardTitle className="newdashboard-audit-card-title">
              Total Open Points
            </CardTitle>
            <AlertCircle className="newdashboard-audit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-audit-card-value newdashboard-audit-open">
              {summary.total_open_points.count}
            </div>
            <p className="newdashboard-audit-note">
              {summary.total_open_points.label}
            </p>
          </CardContent>
        </Card>

        <Card
          className="newdashboard-card-clickable"
          onClick={() => handleCardClick("closed_points")}
          style={{ cursor: "pointer" }}
        >
          <CardHeader className="newdashboard-audit-card-header">
            <CardTitle className="newdashboard-audit-card-title">
              Total Closed Points
            </CardTitle>
            <CheckCircle2 className="newdashboard-audit-icon" />
          </CardHeader>
          <CardContent>
            <div className="newdashboard-audit-card-value newdashboard-audit-closed">
              {summary.total_closed_points.count}
            </div>
            <p className="newdashboard-audit-note">
              {summary.total_closed_points.label}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="newdashboard-audit-charts">
        <Card>
          <CardHeader>
            <CardTitle>Audit Points Summary</CardTitle>
            <CardDescription>Open vs Closed points</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                open: { label: "Open Points", color: "hsl(38, 92%, 50%)" },
                closed: { label: "Closed Points", color: "hsl(142, 76%, 36%)" },
                total: { label: "Total Points", color: "hsl(221, 83%, 53%)" },
              }}
              className="newdashboard-audit-chart"
            >
              {auditPointsSummary.total_points > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      {
                        total: auditPointsSummary.total_points,
                        closed: auditPointsSummary.closed_points,
                        open: auditPointsSummary.open_points,
                        category: "Total",
                      },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar
                      dataKey="total"
                      fill="var(--color-total)"
                      name="Total Points"
                    />
                    <Bar
                      dataKey="closed"
                      fill="var(--color-closed)"
                      name="Closed Points"
                    />
                    <Bar
                      dataKey="open"
                      fill="var(--color-open)"
                      name="Open Points"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Audit Closure Trend</CardTitle>
            <CardDescription>Open vs Closed points over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={{
                open: { label: "Open Points", color: "hsl(38, 92%, 50%)" },
                closed: { label: "Closed Points", color: "hsl(142, 76%, 36%)" },
              }}
              className="newdashboard-audit-chart"
            >
              {auditClosureTrend?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={auditClosureTrend.map((item) => ({
                      month: item.month,
                      open: item.open_points,
                      closed: item.closed_points,
                    }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <ChartTooltip content={<ChartTooltipContent />} />
                    <Legend />
                    <Bar
                      dataKey="closed"
                      fill="var(--color-closed)"
                      name="Closed Points"
                    />
                    <Bar
                      dataKey="open"
                      fill="var(--color-open)"
                      name="Open Points"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="newdashboard-no-data">No Data Found</div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Audit-wise Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Audit-wise Point Status</CardTitle>
          <CardDescription>
            Open, closed, and total points by audit type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              open: { label: "Open Points", color: "hsl(38, 92%, 50%)" },
              closed: { label: "Closed Points", color: "hsl(142, 76%, 36%)" },
              total: { label: "Total Points", color: "hsl(221, 83%, 53%)" },
            }}
            className="newdashboard-audit-chart-lg"
          >
            {auditWisePointStatus?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={auditWisePointStatus.map((item) => ({
                    audit: item.audit_type,
                    open: item.open_points,
                    closed: item.closed_points,
                    total: item.total_points,
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="audit"
                    angle={-15}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Legend />
                  <Bar
                    dataKey="total"
                    fill="var(--color-total)"
                    name="Total Points"
                  />
                  <Bar
                    dataKey="closed"
                    fill="var(--color-closed)"
                    name="Closed Points"
                  />
                  <Bar
                    dataKey="open"
                    fill="var(--color-open)"
                    name="Open Points"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="newdashboard-no-data">No Data Found</div>
            )}
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
}